import { EmptyQueryResults } from '@/components/emptyQueryResults';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { SUPPORT_EMAIL_LINK } from '@/constants/links';
import { useContactSupport } from '@/features/support-contact';
import { useEmailLink } from '@/hooks/useEmailLink';

type DirectoryEmptyStateProps = {
  onResetSearch: () => void;
};

/**
 * What the directory shows when a search or filter matches no expert. It is the shared
 * empty-results state, so it reads the same as the Inquiry Library's, with the directory's own
 * extra way out underneath: asking support for an expert who is not listed. The heading keeps
 * the exact words the end-to-end suites and the Lights-On monitor look for, which they read from
 * the directory contract, so changing it means changing that contract and every deployed build
 * at once.
 */
export function DirectoryEmptyState({ onResetSearch }: DirectoryEmptyStateProps) {
  const { openEmailLink } = useEmailLink();

  const { canUseThread, contact } = useContactSupport();

  return (
    <EmptyQueryResults
      title="No results found."
      text="Check your spelling, or try a different search or filter"
      cta="Reset search"
      onCtaPress={onResetSearch}>
      <VStack space="sm" className="items-center">
        <Text className="text-center text-typography-700">
          We're always improving. Looking for a particular expert or cancer type?
        </Text>

        <Link
          onPress={() => {
            if (canUseThread) {
              contact({
                source: 'directory_empty',
                draft: 'I’m looking for an expert who isn’t listed: ',
              });

              return;
            }

            openEmailLink(SUPPORT_EMAIL_LINK).catch(() => undefined);
          }}>
          <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
            {canUseThread ? 'Send us a message' : 'Send us an email'}
          </LinkText>
        </Link>
      </VStack>
    </EmptyQueryResults>
  );
}
